import { useState, useEffect, useRef, useCallback } from 'react';
import { clearAllCache } from '../services/cache';
import {
  fetchFormPerformance,
  fetchEmailMarketing,
} from '../services/hubspotData';
import type {
  FormPerformanceData,
  EmailMarketingData,
} from '../types/hubspot';

interface UseHubSpotMarketingResult {
  forms: FormPerformanceData | null;
  email: EmailMarketingData | null;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

export function useHubSpotMarketing(): UseHubSpotMarketingResult {
  const [forms, setForms] = useState<FormPerformanceData | null>(null);
  const [email, setEmail] = useState<EmailMarketingData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const refetch = useCallback(() => {
    clearAllCache();
    setRefreshKey((k) => k + 1);
  }, []);

  useEffect(() => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    let cancelled = false;

    async function loadAll() {
      setLoading(true);
      setError(null);

      const results = await Promise.allSettled([
        fetchFormPerformance(controller.signal),
        fetchEmailMarketing(controller.signal),
      ]);

      if (cancelled) return;

      const [formsRes, emailRes] = results;

      if (formsRes.status === 'fulfilled') setForms(formsRes.value);
      if (emailRes.status === 'fulfilled') setEmail(emailRes.value);

      const failures = results.filter((r) => r.status === 'rejected');
      if (failures.length === results.length) {
        const firstErr = (failures[0] as PromiseRejectedResult).reason;
        setError(firstErr instanceof Error ? firstErr.message : 'Failed to load HubSpot data');
      }

      setLoading(false);
    }

    loadAll();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [refreshKey]);

  return { forms, email, loading, error, refetch };
}
